import React, { useEffect, useState, useMemo } from "react";
import useStore from "../../../useStore";
import Loader from "../../Elements/Loader/Loader";
import "./buscadorProductos.css";
import SearchInput from "../../Elements/Search/Search";

const ALMACENES_MOSTRAR = [
  "CENTRAL",
  "ANEXO 2",
  "ANX-1-DISPONIBLE",
  "ESPECIALES",
  "ESPECIALES-ANX2",
];

const useDebounce = (value, delay = 250) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
};

const BuscadorProducto = () => {
  const { fetchTodosLosProductos, productosTotales, loading, error } = useStore();
  const [busqueda, setBusqueda] = useState("");
  const debouncedSearch = useDebounce(busqueda);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    fetchTodosLosProductos();
  }, [fetchTodosLosProductos]);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 5);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const productosFiltrados = useMemo(() => {
    const terms = debouncedSearch
      .toLowerCase()
      .trim()
      .split(/\s+/)
      .filter(Boolean);

    if (terms.length === 0) return productosTotales;

    return productosTotales.filter((prod) => {
      const almacenesFiltrados = prod.almacenes.filter((a) =>
        ALMACENES_MOSTRAR.includes(a.nombre?.toUpperCase())
      );
      const haystack = [
        prod.id?.toString(),
        prod.codigo,
        prod.descripcion,
        ...almacenesFiltrados.map((a) => a.nombre),
      ]
        .join(" ")
        .toLowerCase();

      return terms.every((w) => haystack.includes(w));
    });
  }, [debouncedSearch, productosTotales]);

  return (
    <div className="bp-container container-mh">
      <div className={`bp-header ${scrolled ? "scrolled" : ""}`}>
        <h2 className="bp-title">Productos disponibles</h2>
        <div className="bp-search-wrapper">
          <span className="bp-search-icon bi bi-search" />
          <SearchInput
            value={busqueda}
            onChange={setBusqueda}
            onClear={() => setBusqueda("")}
            placeholder="Buscar por ID, código, descripción o almacén"
            label=""
            type="text"
            typeLabel="Buscar"
            showClear={true}
          />
        </div>
      </div>

      {loading && <Loader />}

      {!loading && error && (
        <div className="alert alert-danger mt-3">Error: {error}</div>
      )}

      {!loading && !error && (
        <>
          {productosFiltrados.length === 0 && (
            <p className="text-muted mt-4 text-center">
              No se encontraron productos.
            </p>
          )}

          <div className="bp-grid">
            {productosFiltrados.map((producto) => {
              const almacenes = producto.almacenes.filter((a) =>
                ALMACENES_MOSTRAR.includes(a.nombre?.toUpperCase())
              );
              const totalStock = almacenes.reduce((sum, a) => sum + a.total, 0);

              return (
                <article key={producto.id} className="bp-card">
                  {producto.imagen && (
                    <div className="bp-img-wrapper">
                      <img
                        src={`https://enlactec.qualityseal.mx/htdocs${producto.imagen}`}
                        alt={producto.descripcion}
                        className="bp-img"
                        loading="lazy"
                      />
                    </div>
                  )}

                  <div className="bp-body">
                    <h5 className="bp-code">{producto.codigo}</h5>
                    <p className="bp-desc">{producto.descripcion}</p>

                    <div className="bp-stock-bar">
                      <div
                        className="bp-stock-fill"
                        style={{
                          width: `${Math.min(totalStock / 1000, 1) * 100}%`,
                        }}
                      />
                    </div>
                    <small className="bp-stock-total">
                      Stock total visible: {totalStock.toLocaleString("es-MX")}
                    </small>

                    <div className="bp-tags">
                      {almacenes.map((alm, i) => (
                        <span key={i} className="bp-tag">
                          {alm.nombre}
                          <strong className="ms-1">
                            {alm.total.toLocaleString("es-MX")}
                          </strong>
                        </span>
                      ))}
                    </div>

                    <footer className="bp-footer">
                      <small>ID: {producto.id}</small>
                    </footer>
                  </div>
                </article>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default BuscadorProducto;
